import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import { FunctionComponent, useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { whatsNew } from "./news.data";

const STORAGE_KEY = "mia.whatsnew.seen";

/** L'utente ha gia' visto l'annuncio corrente? */
export const whatsNewSeen = (): boolean => {
  try {
    return localStorage.getItem(STORAGE_KEY) === whatsNew.id;
  } catch {
    // Safari in navigazione privata puo' negare localStorage: meglio non mostrare nulla che
    // rimostrare lo stesso popup a ogni caricamento.
    return true;
  }
};

const markSeen = () => {
  try {
    localStorage.setItem(STORAGE_KEY, whatsNew.id);
  } catch {
    /* nessun modo di ricordarlo: pazienza, non e' un dato importante */
  }
};

/** Riapre l'annuncio anche a chi l'ha gia' chiuso (usato dalla pagina Novita'). */
export const reopenWhatsNew = () => {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* no-op */
  }
  window.dispatchEvent(new Event("mia:whatsnew:open"));
};

interface OwnProps {}
export type Props = OwnProps;

/**
 * Annuncio "cosa e' cambiato", mostrato una volta sola dopo un rilascio.
 *
 * Si ricorda su localStorage con la chiave del rilascio (`whatsNew.id`): cambiare quell'id lo fa
 * ricomparire a tutti. Non e' una notifica di sistema e non passa dal backend — non ha stato da
 * sincronizzare fra dispositivi, e va bene cosi': se qualcuno lo rivede da un altro browser,
 * il costo e' un clic.
 *
 * Si puo' sempre rileggere dalla pagina Novita' (`reopenWhatsNew`).
 */
const WhatsNewDialog: FunctionComponent<Props> = () => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (!whatsNewSeen()) setOpen(true);
    const onOpen = () => setOpen(true);
    window.addEventListener("mia:whatsnew:open", onOpen);
    return () => window.removeEventListener("mia:whatsnew:open", onOpen);
  }, []);

  const chiudi = useCallback(() => {
    markSeen();
    setOpen(false);
  }, []);

  const vaiAlleNovita = useCallback(() => {
    chiudi();
    navigate("/news");
  }, [chiudi, navigate]);

  return (
    <Dialog open={open} onClose={chiudi} maxWidth="sm" fullWidth scroll="paper">
      <DialogTitle sx={{ pr: 6, fontWeight: 800 }}>
        {whatsNew.title}
        <IconButton
          aria-label="Chiudi"
          onClick={chiudi}
          sx={{ position: "absolute", right: 8, top: 8 }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 2.5 }}>
          {whatsNew.intro}
        </Typography>

        {whatsNew.bullets.map((b, i) => (
          <Box key={i} sx={{ display: "flex", gap: 1.5, mb: 2.5 }}>
            <CheckCircleRoundedIcon color="primary" sx={{ mt: 0.3, flexShrink: 0 }} />
            <Box>
              <Typography variant="subtitle1" fontWeight={700}>
                {b.title}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {b.text}
              </Typography>
            </Box>
          </Box>
        ))}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={vaiAlleNovita}>Vedi tutte le novità</Button>
        <Button variant="contained" onClick={chiudi}>
          Ho capito
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default WhatsNewDialog;
